/**
 * Healer triage (PLAN §7): dispel first, then the external on a tank-buster, then the
 * group cooldown (3+ allies under 60% or a group pulse being cast), then single-target
 * healing by missing HP + predicted incoming damage over the next 2 s. Mana-aware:
 * below 30% mana the bot stops topping people off with the expensive cast heal.
 */
import type { AbilityDef, Command, DeepReadonly, EffectSpec, UnitId } from '@keystone/sim';
import type { ClassKit } from '../kits';
import { noticed, reactionRoll, type BotMemory } from '../memory';
import { abilityLocked, auraRemaining, castTargetId, findAura, hasAura, hpPct, missingHp, resource, resourcePct, type Perception, type RUnit } from '../perception';
import { shapeContains } from '../geometry';
import { bustersOn } from './defensive';
import { healAbsorbOn, safeToDispel } from './mechanics';

/** Group heal after a pulse lands: wait this long, give up after the window. */
const PULSE_HEAL_DELAY_MS = 150;
const PULSE_HEAL_WINDOW_MS = 3000;

const PREDICT_MS = 2000;
/** Burst follow-up (Sunfall): fire at this many stacks, or with this little of the window left. */
const BURST_FULL_STACKS = 5;
const BURST_LAST_CALL_MS = 3000;
/** Group heal on cooldown when 2+ allies are below this (competent+). */
const GROUP_HEAL_SPREAD_HP = 0.85;
const RECENT_MS = 3000;

/** Rough raw damage of an effect list on `target` (base + % max HP, scaled by the caster). */
function estimateDamage(effects: readonly DeepReadonly<EffectSpec>[], caster: RUnit | undefined, target: RUnit): number {
  let total = 0;
  for (const e of effects) {
    if (e.type === 'damage') {
      const a = e.amount;
      total += ((a.base ?? 0) + (a.coef ?? 0) * (caster?.stats.power ?? 0)) * (caster?.scaling.damage ?? 1) + (a.pctTargetMaxHp ?? 0) * target.maxHp;
    } else if (e.type === 'telegraph') {
      total += estimateDamage(e.effects, caster, target);
    }
  }
  return total;
}

/** Damage we expect `u` to take in the next 2 s (recent rate + visible incoming casts/telegraphs). */
export function predictedIncoming(p: Perception, u: RUnit): number {
  let recent = 0;
  for (const d of u.recentDamage) if (d.at > p.now - RECENT_MS) recent += d.amount;
  let incoming = (recent * PREDICT_MS) / RECENT_MS;
  for (const e of p.engaged) {
    if (!e.cast || castTargetId(e) !== u.id || e.cast.endsAt > p.now + PREDICT_MS) continue;
    const ab = p.view.content.ability(e.cast.abilityId);
    if (ab) incoming += estimateDamage(ab.effects, e, u);
  }
  for (const t of p.world.telegraphs) {
    if (t.resolvesAt > p.now + PREDICT_MS || !shapeContains(t.area, u.pos, u.radius)) continue;
    const src = p.world.units[t.sourceId];
    if (src && src.faction !== u.faction) incoming += estimateDamage(t.effects, src, u);
  }
  return incoming;
}

function dispelTypesOf(ab: AbilityDef): string[] {
  const out: string[] = [];
  const walk = (effects: readonly EffectSpec[]): void => {
    for (const e of effects) {
      if (e.type === 'dispel' && e.harmful) out.push(...e.dispelTypes);
      if (e.type === 'conditional') {
        walk(e.then);
        walk(e.else ?? []);
      }
    }
  };
  walk(ab.effects);
  return out.length > 0 ? out : ['magic', 'poison'];
}

function castOn(p: Perception, abilityId: string | undefined, targetId: UnitId | null): Command[] | null {
  if (!abilityId) return null;
  const ab = p.view.content.ability(abilityId);
  if (!ab || abilityLocked(p.view, p.self, abilityId) || !p.view.queries.canCast(p.self.id, abilityId, targetId, null).ok) return null;
  const cmds: Command[] = [];
  const hasCastTime = ab.cast && ab.cast.type !== 'instant';
  if (hasCastTime && !ab.canCastWhileMoving && p.self.move) cmds.push({ type: 'stop', actorId: p.self.id });
  cmds.push({ type: 'cast', actorId: p.self.id, abilityId, ...(targetId ? { targetId } : {}) });
  return cmds;
}

export function planDispel(p: Perception, kit: ClassKit, mem: BotMemory): Command[] | null {
  if (!kit.dispel) return null;
  const ab = p.view.content.ability(kit.dispel);
  if (!ab) return null;
  const types = dispelTypesOf(ab);
  // A debuff is noticed `reactionMs` after it lands (like a cast bar).
  // +encounters: each holder is ranked by its worst dispellable debuff (dispelUrgency), then HP.
  const ranked: { u: RUnit; rank: number }[] = [];
  for (const u of p.party) {
    let rank = Infinity;
    for (const a of u.auras) {
      const def = p.view.content.aura(a.auraId);
      if (!def || def.kind !== 'debuff' || !def.dispelType || !types.includes(def.dispelType)) continue;
      // Skill dial: per-debuff reaction (+ extra dispel delay); a missed roll is never dispelled.
      const rx = reactionRoll(mem, `disp:${u.id}:${a.iid}`);
      if (!rx.ok || p.now < a.appliedAt + rx.at + mem.skill.dispelDelayMs) continue;
      // Discipline: only what matters (the cooldown is better saved for CC and real stacks).
      if (mem.skill.dispelDiscipline && !worthDispelling(p, u, a)) continue;
      // +encounters: jump-on-dispel debuffs wait until the holder is clear of allies (or low).
      if (!safeToDispel(p, u, a.auraId)) continue;
      rank = Math.min(rank, dispelUrgency(p, u, a.auraId, a.stacks));
    }
    if (rank < Infinity) ranked.push({ u, rank });
  }
  ranked.sort((a, b) => a.rank - b.rank || hpPct(a.u) - hpPct(b.u));
  for (const { u } of ranked) {
    const cmds = castOn(p, kit.dispel, u.id);
    if (cmds) return cmds;
  }
  return null;
}

/** Stacking DoTs: dispel at this fraction of max stacks (at least 2), or when the holder is low. */
const DISPEL_STACK_FRAC = 0.5;
const DISPEL_LOW_HP = 0.5;
/** Plain DoTs / heal absorbs: dispel when the holder is below this, with this much left. */
const DISPEL_DOT_HP = 0.7;
const DISPEL_DOT_MIN_LEFT_MS = 3000;

/**
 * Dispel discipline (competent+): CC, jump/isolate debuffs and non-periodic debuffs always; a
 * stacking DoT once it has real stacks; a plain DoT or heal absorb only on a hurt holder with time
 * left on it. Everything else is healed through.
 */
export function worthDispelling(p: Perception, holder: RUnit, a: RUnit['auras'][number]): boolean {
  const def = p.view.content.aura(a.auraId);
  if (!def) return true;
  if (def.cc || def.jumpOnDispel || def.hazard?.intent === 'isolate') return true;
  const hp = hpPct(holder);
  if (def.periodic && def.stacking === 'stack' && (def.maxStacks ?? 0) > 1) {
    return hp < DISPEL_LOW_HP || a.stacks >= Math.max(2, Math.ceil(DISPEL_STACK_FRAC * (def.maxStacks ?? 1)));
  }
  if (def.periodic || def.healAbsorb) {
    const left = a.expiresAt === null ? Infinity : a.expiresAt - p.now;
    return hp < DISPEL_LOW_HP || (hp < DISPEL_DOT_HP && left >= DISPEL_DOT_MIN_LEFT_MS);
  }
  return true;
}

/**
 * +encounters: dispel order (lower first):
 *   0    crowd control (a stunned/disoriented player does nothing, an incapacitated kicker misses kicks);
 *   1    jump-on-dispel debuffs once the holder has stepped clear (−healing, ticking, and the
 *        holder can't rejoin the group until it's gone);
 *   1–2  stacking DoTs, more stacks first;
 *   2.5  other DoTs; 3 anything else.
 * A holder below 35% HP moves up one band.
 */
export function dispelUrgency(p: Perception, holder: RUnit, auraId: string, stacks: number): number {
  const def = p.view.content.aura(auraId);
  if (!def) return 3;
  let r = 3;
  if (def.cc) r = 0;
  else if (def.jumpOnDispel || def.hazard?.intent === 'isolate') r = 1;
  else if (def.periodic && def.stacking === 'stack' && (def.maxStacks ?? 0) > 1) r = 2 - Math.min(1, stacks / (def.maxStacks ?? 1));
  else if (def.periodic || def.healAbsorb) r = 2.5;
  if (hpPct(holder) < 0.35) r -= 1;
  return r;
}

/**
 * +encounters: pre-ramp (a DBM-style read of the boss's `ai.timers`): an ability flagged
 * `counter: 'healerCd'` lands within PRE_RAMP_MS, so top people off before it.
 */
const PRE_RAMP_MS = 4000;
export function preRamp(p: Perception): boolean {
  for (const e of p.engaged) {
    const timers = e.ai?.timers;
    if (!timers || (e.tier !== 'boss' && e.tier !== 'miniboss')) continue;
    for (const id of Object.keys(timers)) {
      const due = timers[id]! - p.now;
      if (due < 0 || due > PRE_RAMP_MS) continue;
      if (p.view.content.enemyAbility(id)?.counter?.includes('healerCd')) return true;
    }
    if (e.cast && p.view.content.enemyAbility(e.cast.abilityId)?.counter?.includes('healerCd')) return true;
  }
  return false;
}

export interface TriageEntry {
  unit: RUnit;
  missing: number;
  predicted: number;
  score: number;
}

/** Party members ranked by missing HP + predicted incoming (highest first). */
export function triage(p: Perception): TriageEntry[] {
  return p.party
    .map((unit) => {
      const missing = missingHp(unit) + healAbsorbOn(p, unit); // +encounters: heal absorbs count as missing HP
      const predicted = predictedIncoming(p, unit);
      return { unit, missing, predicted, score: missing + predicted };
    })
    .sort((a, b) => b.score - a.score || hpPct(a.unit) - hpPct(b.unit));
}

export function planHeal(p: Perception, kit: ClassKit, mem: BotMemory): Command[] | null {
  const dispel = planDispel(p, kit, mem);
  if (dispel) return dispel;

  const inCombat = p.engaged.length > 0;
  const tank = p.tank;
  if (inCombat && tank?.alive && kit.externalDefensive) {
    const shielded = kit.externalDefensiveAura ? tank.auras.some((a) => a.auraId === kit.externalDefensiveAura) : false;
    if (!shielded && bustersOn(p, tank.id, mem.skill.reactionMs).length > 0) {
      const cmds = castOn(p, kit.externalDefensive, tank.id);
      if (cmds) return cmds;
    }
  }

  // +ranger-specs: a heal-over-time healer plans its own HoTs (specKits/ranger.ts); null falls through.
  if (kit.healPlan) {
    const own = kit.healPlan(p, mem);
    if (own) return own;
  }

  if (inCombat && kit.groupHeal) {
    const hurt = p.party.filter((u) => hpPct(u) < 0.6).length;
    // Group pulses: note when the cast lands and heal just after, not when it starts.
    for (const e of p.engaged) {
      if (e.cast && p.now >= e.cast.startedAt + mem.skill.reactionMs && p.view.content.enemyAbility(e.cast.abilityId)?.kind === 'pulse') {
        mem.pulseLandsAt = Math.max(mem.pulseLandsAt ?? 0, e.cast.endsAt);
      }
    }
    let pulse = false;
    if (mem.pulseLandsAt !== null && p.now >= mem.pulseLandsAt + PULSE_HEAL_DELAY_MS) {
      pulse = p.now <= mem.pulseLandsAt + PULSE_HEAL_WINDOW_MS && p.party.some((u) => hpPct(u) < 0.9);
      if (!pulse) mem.pulseLandsAt = null;
    }
    // Finesse: the group heal is also a rotational cooldown, pressed whenever two allies are hurt.
    const spread = mem.skill.aplFinesse && p.party.filter((u) => u.alive && hpPct(u) < GROUP_HEAL_SPREAD_HP).length >= 2;
    if (noticed(mem, 'heal:group', hurt >= 3 || spread, p.now) || pulse) {
      const cmds = castOn(p, kit.groupHeal, p.self.id);
      if (cmds) {
        mem.pulseLandsAt = null;
        return cmds;
      }
    }
  }

  const h = kit.heals ?? {};
  const self = p.self;
  const power = h.spenderResource ? resource(self, h.spenderResource) : 0;
  const powerMax = h.spenderResource ? (self.resources[h.spenderResource]?.max ?? 0) : 0;
  const spenderReady = !!h.spenderResource && power >= (h.spenderCost ?? 3);
  const free = !!h.freeSpenderAura && hasAura(self, h.freeSpenderAura);
  const hurt = (below: number) => p.party.filter((u) => u.alive && hpPct(u) < below).length;

  // F13 burst follow-up (Sunfall): at full Dawnlight, when the window is about to close, or when
  // the group is hurt (lightbinder.md §6).
  if (inCombat && h.burst && h.burstFollowupAura && hasAura(self, h.burstFollowupAura)) {
    const stacks = h.burstFollowupStacks ? (findAura(self, h.burstFollowupStacks)?.stacks ?? 0) : 1;
    const left = auraRemaining(self, h.burstFollowupAura, p.now);
    if (stacks > 0 && (stacks >= BURST_FULL_STACKS || left < BURST_LAST_CALL_MS || (stacks >= 3 && hurt(0.7) >= 3))) {
      const tgt = self.targetId && p.world.units[self.targetId]?.alive && p.world.units[self.targetId]?.faction !== self.faction ? self.targetId : self.id;
      const cmds = castOn(p, h.burst, tgt);
      if (cmds) return cmds;
    }
  }
  // AoE spender (Dawnsong): free, or affordable with 3+ allies below 85%.
  if (inCombat && h.aoeSpender && (free || spenderReady) && hurt(0.85) >= 3) {
    const cmds = castOn(p, h.aoeSpender, self.id);
    if (cmds) return cmds;
  }

  const manaLow = h.manaResource ? resourcePct(self, h.manaResource) < 0.3 : false;
  const ramp = inCombat && !manaLow && preRamp(p); // +encounters: top off before planned group damage
  // Near the cap, a generator heal would waste power: spend it on smaller deficits too.
  // Inside the burst window every spender also builds the follow-up's stacks (Dawnlight).
  const building = !!h.burstFollowupAura && hasAura(self, h.burstFollowupAura);
  const nearCap = powerMax > 0 && (power >= powerMax - 1 || building);
  // AoE pulls: spend on the group spender instead of the single-target one.
  const aoePull = !!h.aoeSpender && p.engaged.length >= 3 && hurt(0.9) >= 2;
  if (inCombat && aoePull && (free || spenderReady) && (nearCap || free)) {
    const cmds = castOn(p, h.aoeSpender, self.id);
    if (cmds) return cmds;
  }
  const infused = !!h.infusionAura && hasAura(self, h.infusionAura);
  for (const t of triage(p)) {
    const frac = t.score / Math.max(1, t.unit.maxHp);
    const missFrac = t.missing / Math.max(1, t.unit.maxHp);
    if (frac < 0.08 || t.missing <= 0) continue;
    const options: (string | undefined)[] = [];
    if ((spenderReady || free) && (missFrac >= 0.25 || ((nearCap || free) && missFrac >= (power >= powerMax ? 0.05 : 0.1)))) options.push(h.spender);
    if (infused && missFrac >= 0.1) options.push(h.flash);
    // A generator heal at full power wastes the power: only for a real deficit then.
    if (frac >= 0.12 && (powerMax === 0 || power < powerMax || missFrac >= 0.3)) options.push(h.shock);
    // +acolyte-specs: per-kit flash threshold (flashAt) and no self-flash (flashNotSelf).
    const flashOk = !(h.flashNotSelf && t.unit.id === self.id) && !(h.flashSelfFloor && hpPct(self) < h.flashSelfFloor.hp && hpPct(t.unit) >= h.flashSelfFloor.allyBelow);
    if (flashOk && (inCombat ? frac >= (manaLow ? Math.max(0.4, h.flashAt ?? 0) : ramp ? Math.min(0.1, h.flashAt ?? 0.1) : (h.flashAt ?? 0.2)) : missFrac >= 0.1 && !manaLow)) options.push(h.flash);
    for (const id of options) {
      const cmds = castOn(p, id, t.unit.id);
      if (cmds) return cmds;
    }
  }
  return null;
}
